import { ZodSchema } from 'zod';
import type { ZodError } from 'zod';
import type { Resolver } from 'react-hook-form';
import i18n from '../i18n';

/**
 * Custom validation error class for formatted errors
 */
export class ValidationError extends Error {
  public errors: Record<string, string>;

  constructor(errors: Record<string, string>) {
    super('Validation failed');
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

/**
 * Formats Zod error into a key-value object with translated messages
 */
function formatZodError(error: ZodError): Record<string, string> {
  const errors: Record<string, string> = {};

  error.issues.forEach(issue => {
    const path = issue.path.join('.') || 'form';
    // The error message from schema is an i18n key
    const message = i18n.t(issue.message);
    errors[path] = message;
  });

  return errors;
}

/**
 * Validates data against a Zod schema
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @returns Validated and typed data
 * @throws ValidationError with formatted errors
 */
export function validateForm<T>(schema: ZodSchema<T>, data: unknown): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    const formattedErrors = formatZodError(result.error);
    throw new ValidationError(formattedErrors);
  }

  return result.data;
}

/**
 * Validates API response data against a Zod schema
 * @param schema - Zod schema to validate against
 * @param data - Response data to validate
 * @returns Validated and typed data
 * @throws ZodError if validation fails
 */
export function validateApiResponse<T>(schema: ZodSchema<T>, data: unknown): T {
  return schema.parse(data);
}

/**
 * Safe API validation that returns null instead of throwing
 * @param schema - Zod schema to validate against
 * @param data - Response data to validate
 * @returns Validated data or null if validation fails
 */
export function safeValidateApiResponse<T>(schema: ZodSchema<T>, data: unknown): T | null {
  const result = schema.safeParse(data);
  return result.success ? result.data : null;
}

/**
 * Creates a form validation hook result compatible with react-hook-form
 * This converts Zod errors to the format expected by react-hook-form
 */
export function zodToFormErrors(
  zodError: ZodError
): Record<string, { type: string; message: string }> {
  const errors: Record<string, { type: string; message: string }> = {};

  zodError.issues.forEach(issue => {
    const path = issue.path.join('.');
    errors[path] = {
      type: 'validation',
      message: i18n.t(issue.message),
    };
  });

  return errors;
}

/**
 * Type guard to check if error is a ZodError
 */
export function isZodError(error: unknown): error is ZodError {
  return error instanceof Error && 'issues' in error;
}

/**
 * Type guard to check if error is a ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Creates a Zod resolver for react-hook-form with i18n support
 * This resolver translates Zod error messages using i18next
 * @param schema - Zod schema to validate against
 * @returns Resolver function compatible with react-hook-form
 */
export function zodI18nResolver<T extends Record<string, unknown>>(
  schema: ZodSchema<T>
): Resolver<T> {
  return async (data, _context, _options) => {
    const result = await schema.safeParseAsync(data);

    if (!result.success) {
      const errors: Record<string, { type: string; message: string }> = {};

      result.error.issues.forEach(issue => {
        const path = issue.path.join('.');
        // Translate the error message using i18n
        const translatedMessage = i18n.t(issue.message);
        errors[path] = {
          type: 'validation',
          message: translatedMessage,
        };
      });

      return {
        values: {} as T,
        errors: errors as any,
      };
    }

    return {
      values: result.data,
      errors: {},
    };
  };
}
